<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { formatToTitleCase } from 'dashboard/helper/commons';
import { formatDurationMinutes } from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  description: { type: String, default: '' },
  assignmentOrder: { type: String, default: '' },
  conversationPriority: { type: String, default: '' },
  fairDistributionLimit: { type: Number, default: null },
  fairDistributionWindow: { type: Number, default: null },
  excludeOlderThanHours: { type: Number, default: null },
  excludeAdministrators: { type: Boolean, default: false },
  enabled: { type: Boolean, default: true },
  assignedInboxCount: { type: Number, default: 0 },
  inboxes: { type: Array, default: () => [] },
  isFetchingInboxes: { type: Boolean, default: false },
});

const emit = defineEmits(['edit', 'delete', 'fetchInboxes']);

const { t, te } = useI18n();

const FORM_KEY = 'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.FORM';
const REDESIGN_KEY = 'ASSIGNMENT_POLICY.REDESIGN';

const showInboxes = ref(false);

const optionLabel = (type, value) => {
  if (!value) return '';
  const key = `${FORM_KEY}.${type}.${value.toUpperCase()}.LABEL`;
  return te(key) ? t(key) : formatToTitleCase(value);
};

const inboxItems = computed(() =>
  props.inboxes.map(inbox => ({
    name: inbox.name,
    id: inbox.id,
    icon: getInboxIconByType(
      inbox.channelType,
      inbox.medium,
      'line',
      inbox.voiceEnabled
    ),
  }))
);

const details = computed(() => {
  const items = [];
  const order = optionLabel('ASSIGNMENT_ORDER', props.assignmentOrder);
  const priority = optionLabel(
    'ASSIGNMENT_PRIORITY',
    props.conversationPriority
  );
  if (order) {
    items.push({
      key: 'order',
      icon: 'i-hugeicons-user-switch',
      label: `${t(`${REDESIGN_KEY}.ASSIGNMENT.ORDER`)}: ${order}`,
    });
  }
  if (priority) {
    items.push({
      key: 'priority',
      icon: 'i-hugeicons-sorting-05',
      label: `${t(`${REDESIGN_KEY}.ASSIGNMENT.PRIORITY`)}: ${priority}`,
    });
  }
  const window = formatDurationMinutes(
    (props.fairDistributionWindow || 0) / 60,
    t
  );
  if (props.fairDistributionLimit && window) {
    items.push({
      key: 'fair',
      icon: 'i-hugeicons-balance-scale',
      label: t(`${REDESIGN_KEY}.ASSIGNMENT.FAIR`, {
        limit: props.fairDistributionLimit,
        window,
      }),
    });
  }
  const stale = formatDurationMinutes(
    (props.excludeOlderThanHours || 0) * 60,
    t
  );
  if (stale) {
    items.push({
      key: 'stale',
      icon: 'i-hugeicons-clock-01',
      label: t(`${REDESIGN_KEY}.ASSIGNMENT.STALE`, { duration: stale }),
    });
  }
  if (props.excludeAdministrators) {
    items.push({
      key: 'admins',
      icon: 'i-hugeicons-user-block-01',
      label: t(`${REDESIGN_KEY}.ASSIGNMENT.EXCLUDES_ADMINS`),
    });
  }
  return items;
});

const handleEdit = () => {
  emit('edit', props.id);
};

const handleDelete = () => {
  emit('delete', props.id);
};

const toggleInboxes = () => {
  showInboxes.value = !showInboxes.value;
  if (showInboxes.value && !props.inboxes?.length) {
    emit('fetchInboxes', props.id);
  }
};
</script>

<template>
  <article
    class="flex min-w-0 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4 transition-colors hover:border-n-strong motion-reduce:transition-none"
    :data-test-id="`assignment-policy-card-${id}`"
  >
    <div class="flex min-w-0 items-start justify-between gap-3">
      <div class="flex min-w-0 items-start gap-3">
        <span
          class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-alpha-2 text-n-slate-11"
          aria-hidden="true"
        >
          <Icon
            icon="i-hugeicons-arrow-data-transfer-horizontal"
            class="size-5"
          />
        </span>
        <div class="flex min-w-0 flex-col items-start gap-1">
          <button
            type="button"
            class="max-w-full text-left text-sm font-semibold text-n-slate-12 hover:text-n-brand focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand rounded"
            :title="name"
            @click="handleEdit"
          >
            <span class="line-clamp-2 break-words">{{ name }}</span>
          </button>
          <span
            class="inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-xs font-medium"
            :class="
              enabled
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            <span
              class="size-1.5 rounded-full"
              :class="enabled ? 'bg-n-teal-9' : 'bg-n-slate-9'"
            />
            {{
              t(
                enabled
                  ? `${REDESIGN_KEY}.COMMON.ACTIVE`
                  : `${REDESIGN_KEY}.COMMON.INACTIVE`
              )
            }}
          </span>
        </div>
      </div>
      <div class="flex shrink-0 items-center gap-1">
        <Button
          v-tooltip.top="t(`${REDESIGN_KEY}.COMMON.EDIT`)"
          icon="i-hugeicons-pencil-edit-02"
          sm
          slate
          ghost
          :aria-label="t(`${REDESIGN_KEY}.COMMON.EDIT_ARIA`, { name })"
          data-test-id="policy-edit"
          @click="handleEdit"
        />
        <Button
          v-tooltip.top="t(`${REDESIGN_KEY}.COMMON.DELETE`)"
          icon="i-hugeicons-delete-02"
          sm
          slate
          ghost
          class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
          :aria-label="t(`${REDESIGN_KEY}.COMMON.DELETE`)"
          data-test-id="policy-delete"
          @click="handleDelete"
        />
      </div>
    </div>

    <p
      class="mb-0 line-clamp-2 break-words text-sm leading-relaxed text-n-slate-11"
    >
      {{ description || t(`${REDESIGN_KEY}.COMMON.NO_DESCRIPTION`) }}
    </p>

    <ul v-if="details.length" class="mb-0 flex flex-wrap gap-1.5">
      <li
        v-for="detail in details"
        :key="detail.key"
        class="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-12"
      >
        <Icon :icon="detail.icon" class="size-3.5 shrink-0 text-n-slate-11" />
        <span class="truncate">{{ detail.label }}</span>
      </li>
    </ul>

    <div class="mt-auto flex flex-col gap-2 border-t border-n-weak pt-3">
      <button
        type="button"
        class="flex w-full items-center justify-between gap-2 rounded-md text-sm text-n-slate-11 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand disabled:cursor-default disabled:hover:text-n-slate-11"
        :aria-expanded="showInboxes"
        :disabled="!assignedInboxCount"
        data-test-id="policy-inboxes-toggle"
        @click="toggleInboxes"
      >
        <span class="flex items-center gap-2">
          <Icon icon="i-hugeicons-inbox" class="size-4" />
          {{
            t(
              `${REDESIGN_KEY}.ASSIGNMENT.INBOXES`,
              { n: assignedInboxCount },
              assignedInboxCount
            )
          }}
        </span>
        <span v-if="assignedInboxCount" class="flex items-center gap-1 text-xs">
          {{
            t(
              showInboxes
                ? `${REDESIGN_KEY}.ASSIGNMENT.HIDE_INBOXES`
                : `${REDESIGN_KEY}.ASSIGNMENT.SHOW_INBOXES`
            )
          }}
          <Icon
            :icon="
              showInboxes
                ? 'i-hugeicons-arrow-up-01'
                : 'i-hugeicons-arrow-down-01'
            "
            class="size-3.5"
          />
        </span>
      </button>
      <div v-if="showInboxes">
        <div
          v-if="isFetchingInboxes && !inboxItems.length"
          class="flex justify-center py-2 text-n-slate-11"
        >
          <Spinner />
        </div>
        <ul v-else-if="inboxItems.length" class="mb-0 flex flex-wrap gap-1.5">
          <li
            v-for="inbox in inboxItems"
            :key="inbox.id"
            class="inline-flex min-w-0 max-w-full items-center gap-1.5 rounded-md border border-n-weak px-2 py-1 text-xs text-n-slate-12"
            :title="inbox.name"
          >
            <Icon :icon="inbox.icon" class="size-3.5 shrink-0" />
            <span class="truncate">{{ inbox.name }}</span>
          </li>
        </ul>
        <p v-else class="mb-0 text-xs text-n-slate-11">
          {{ t(`${REDESIGN_KEY}.ASSIGNMENT.NO_INBOXES`) }}
        </p>
      </div>
    </div>
  </article>
</template>
